import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getJson, money, shortDate, statusClass } from '../helpers';

export default function ToolListPage({
  title,
  crumb,
  description,
  projectVuid,
  fetcher,
  columns,
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const loads = [fetcher()];
    if (projectVuid) {
      loads.push(getJson(`/api/projects/${projectVuid}`).catch(() => null));
    }
    Promise.all(loads)
      .then(([data, project]) => {
        if (cancelled) return;
        setRows(Array.isArray(data) ? data : []);
        if (project?.project_name) setProjectName(project.project_name);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || `Unable to load ${title.toLowerCase()}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid, title]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      columns.some((column) => String(row[column.key] || '').toLowerCase().includes(term))
    );
  }, [rows, query, columns]);

  const renderCell = (row, column) => {
    const value = row[column.key];
    if (column.money) return money(value);
    if (column.date) return shortDate(value);
    if (column.status) {
      return <span className={`pc-pill ${statusClass(value)}`}>{value || '—'}</span>;
    }
    return value === 0 ? '0' : value || '—';
  };

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            {title}
          </>
        ) : (
          crumb || title
        )}
      </div>
      <div className="pc-title-row">
        <h1>{title}</h1>
      </div>
      {description ? <p className="pc-muted" style={{ marginTop: 0, marginBottom: 12 }}>{description}</p> : null}
      <div className="pc-toolbar">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Filter ${title.toLowerCase()}`} />
      </div>
      <div className="pc-card">
        {loading ? (
          <div className="pc-empty">Loading…</div>
        ) : error ? (
          <div className="pc-empty">{error}</div>
        ) : visible.length === 0 ? (
          <div className="pc-empty">No {title.toLowerCase()} to show.</div>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={column.money ? 'pc-right' : undefined}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr
                  key={row.id}
                  className={row.href ? 'pc-clickable' : undefined}
                  onClick={() => row.href && navigate(row.href)}
                >
                  {columns.map((column, index) => (
                    <td key={column.key} className={column.money ? 'pc-right' : undefined}>
                      {index === 0 && row.href ? (
                        <Link to={row.href} onClick={(event) => event.stopPropagation()}>
                          {renderCell(row, column)}
                        </Link>
                      ) : (
                        renderCell(row, column)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
